import { useState } from "react";
import { logger } from "@/services/LoggerService";

const hasWindow = typeof window !== "undefined";

const getLocalStorage = (): Storage | null => {
    if (!hasWindow) return null;

    try {
        return window.localStorage;
    } catch (error) {
        console.warn("[useLocalStorage] localStorage is not accessible.", error);
        logger.warn("localStorage is not accessible", {
            context: "useLocalStorage",
            error: String(error),
        });
        return null;
    }
};

const resetArchivedProperties = <T>(initialValue: T): T => {
    const storage = getLocalStorage();
    if (!storage) return initialValue;

    try {
        storage.setItem(
            "archived-properties",
            JSON.stringify(initialValue ?? { playableProperties: [], filteredPlayableProperties: [] }),
        );
    } catch (error) {
        console.warn("[useLocalStorage] Unable to reset 'archived-properties' in localStorage.", error);
        logger.warn("Unable to reset 'archived-properties' in localStorage", {
            context: "useLocalStorage",
            error: String(error),
        });
    }

    return initialValue;
};

const getStoredValue = <T>(key: string, initialValue: T): T => {
    const storage = getLocalStorage();
    if (!storage) return initialValue;

    try {
        if (key === "archived-properties") {
            return resetArchivedProperties(initialValue);
        }

        const item = storage.getItem(key);
        return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
        console.warn(`[useLocalStorage] Unable to read key "${key}" from localStorage.`, error);
        logger.warn(`Unable to read key "${key}" from localStorage`, {
            context: "useLocalStorage",
            key,
            error: String(error),
        });
        return initialValue;
    }
};

// Custom hook to get and set data in localStorage with graceful Safari handling
const useLocalStorage = <T>(key: string, initialValue: T): [T, (value: T) => void, () => void] => {
    const [value, setValue] = useState<T>(() => getStoredValue(key, initialValue));

    const updateValue = (newValue: T): void => {
        setValue(newValue);

        const storage = getLocalStorage();
        if (!storage) return;

        try {
            storage.setItem(key, JSON.stringify(newValue));
        } catch (error: unknown) {
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                console.warn(`[useLocalStorage] Quota exceeded while storing key "${key}". Value kept in memory only.`);
                logger.warn(`Quota exceeded while storing key "${key}"`, {
                    context: "useLocalStorage",
                    key,
                    errorType: "QuotaExceededError",
                });
            } else {
                console.warn(`[useLocalStorage] Unable to set key "${key}" in localStorage.`, error);
                logger.warn(`Unable to set key "${key}" in localStorage`, {
                    context: "useLocalStorage",
                    key,
                    error: String(error),
                });
            }
        }
    };

    const clearValue = (): void => {
        const storage = getLocalStorage();
        if (!storage) return;

        try {
            storage.removeItem(key);
        } catch (error) {
            console.warn(`[useLocalStorage] Unable to remove key "${key}" from localStorage.`, error);
            logger.warn(`Unable to remove key "${key}" from localStorage`, {
                context: "useLocalStorage",
                key,
                error: String(error),
            });
        }
    };

    return [value, updateValue, clearValue];
};

export default useLocalStorage;
